import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Icon,
  Text,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  ScaleFade,
} from '@chakra-ui/react';
import {
  MdClose,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdInfo,
} from 'react-icons/md';

const NotificationStack = ({ notifications = [], onDismiss = null }) => {
  const [visibleNotifications, setVisibleNotifications] = useState(
    notifications.map((notification, index) => ({
      id: `initial-${index}`,
      ...notification,
      visible: true,
    }))
  );
  const [globalNotifications, setGlobalNotifications] = useState([]);

  // Listen for global notification events
  useEffect(() => {
    const handleNotification = (event) => {
      const newNotif = {
        id: `notif-${Date.now()}-${Math.random()}`,
        ...event.detail,
        visible: true,
      };
      setGlobalNotifications((prev) => [...prev, newNotif]);

      // Auto-dismiss after 6 seconds
      setTimeout(() => {
        handleDismiss(newNotif.id);
      }, 6000);
    };

    window.addEventListener('showNotification', handleNotification);
    return () => window.removeEventListener('showNotification', handleNotification);
  }, []);

  // Update initial notifications
  useEffect(() => {
    const updated = notifications.map((notification, index) => ({
      id: `initial-${index}`,
      ...notification,
      visible: true,
    }));
    setVisibleNotifications(updated);
  }, [notifications]);

  // Color mode values - must be at top level
  const bgGradient = useColorModeValue(
    'white',
    '#2A3547'
  );
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const shadowColor = useColorModeValue(
    '0 10px 25px -5px rgba(0, 0, 0, 0.12)',
    '0 10px 25px -5px rgba(0, 0, 0, 0.6)'
  );

  // Error colors
  const errorBg = useColorModeValue('#FEF2F2', '#2D1F1F');
  const errorBorder = useColorModeValue('red.300', 'red.500');
  const errorText = useColorModeValue('red.800', 'red.100');
  const errorIconBg = useColorModeValue('red.100', '#3D2F2F');

  // Warning colors
  const warningBg = useColorModeValue('#FFFAF0', '#2D2415');
  const warningBorder = useColorModeValue('orange.300', 'orange.500');
  const warningText = useColorModeValue('orange.800', 'orange.100');
  const warningIconBg = useColorModeValue('orange.100', '#3D3120');

  // Success colors
  const successBg = useColorModeValue('#F0FFF4', '#1F2D23');
  const successBorder = useColorModeValue('green.300', 'green.500');
  const successText = useColorModeValue('green.800', 'green.100');
  const successIconBg = useColorModeValue('green.100', '#2D3D32');

  // Info colors
  const infoBg = useColorModeValue('#EBF8FF', '#1F2A35');
  const infoBorder = useColorModeValue('blue.300', 'blue.500');
  const infoText = useColorModeValue('blue.800', 'blue.100');
  const infoIconBg = useColorModeValue('blue.100', '#2D3D45');

  // Hover shadow
  const hoverShadow = useColorModeValue(
    '0 20px 35px -5px rgba(0, 0, 0, 0.12)',
    '0 20px 35px -5px rgba(0, 0, 0, 0.6)'
  );

  // Hover background
  const hoverBgGray = useColorModeValue('gray.100', 'gray.700');

  // Text color for message
  const messageTextColor = useColorModeValue('gray.700', 'gray.300');

  // Title color
  const titleTextColor = useColorModeValue('gray.900', 'gray.100');

  const getNotificationConfig = (type = 'error') => {
    const configs = {
      error: {
        icon: MdError,
        bgColor: errorBg,
        iconBgColor: errorIconBg,
        borderColor: errorBorder,
        iconColor: 'red.600',
        textColor: errorText,
      },
      warning: {
        icon: MdWarning,
        bgColor: warningBg,
        iconBgColor: warningIconBg,
        borderColor: warningBorder,
        iconColor: 'orange.600',
        textColor: warningText,
      },
      success: {
        icon: MdCheckCircle,
        bgColor: successBg,
        iconBgColor: successIconBg,
        borderColor: successBorder,
        iconColor: 'green.600',
        textColor: successText,
      },
      info: {
        icon: MdInfo,
        bgColor: infoBg,
        iconBgColor: infoIconBg,
        borderColor: infoBorder,
        iconColor: 'blue.600',
        textColor: infoText,
      },
    };
    return configs[type] || configs.error;
  };

  const handleDismiss = (id) => {
    setVisibleNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, visible: false } : notif))
    );
    setGlobalNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, visible: false } : notif))
    );
    if (onDismiss) {
      onDismiss(id);
    }
  };

  const allNotifications = [...visibleNotifications, ...globalNotifications];
  const visibleItems = allNotifications.filter((n) => n.visible);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={{ base: '80px', md: '20px' }}
      right={{ base: '0', md: '20px' }}
      left={{ base: '0', md: 'auto' }}
      zIndex="2000"
      maxW={{ base: '100%', md: '450px' }}
      px={{ base: '12px', md: '0' }}
    >
      <VStack spacing="12px" align="stretch">
        {visibleItems.map((notification) => {
          const config = getNotificationConfig(notification.type || 'error');
          const Icon_ = config.icon;

          return (
            <ScaleFade key={notification.id} initialScale={0.9} in={notification.visible}>
              <Box
                bg={bgGradient}
                borderLeft={`4px solid`}
                borderLeftColor={config.iconColor}
                borderRadius="12px"
                p="16px"
                boxShadow={shadowColor}
                border={`1px solid`}
                borderColor={config.borderColor}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                _hover={{
                  transform: 'translateX(-4px)',
                  boxShadow: hoverShadow,
                }}
              >
                <HStack spacing="12px" align="flex-start">
                  {/* Icon */}
                  <Flex
                    w="40px"
                    h="40px"
                    minW="40px"
                    borderRadius="10px"
                    bg={config.iconBgColor}
                    align="center"
                    justify="center"
                  >
                    <Icon as={Icon_} w="22px" h="22px" color={config.iconColor} />
                  </Flex>

                  {/* Content */}
                  <Flex flex="1" direction="column" minW="0">
                    {notification.title && (
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        color={config.textColor}
                        mb="4px"
                      >
                        {notification.title}
                      </Text>
                    )}
                    <Text
                      fontSize="sm"
                      color={messageTextColor}
                      lineHeight="1.5"
                      wordBreak="break-word"
                    >
                      {notification.message || notification}
                    </Text>
                    {notification.action && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme={
                          notification.type === 'error'
                            ? 'red'
                            : notification.type === 'warning'
                            ? 'orange'
                            : notification.type === 'success'
                            ? 'green'
                            : 'blue'
                        }
                        mt="8px"
                        h="28px"
                        fontSize="xs"
                        onClick={() => {
                          notification.action.onClick?.();
                          handleDismiss(notification.id);
                        }}
                      >
                        {notification.action.label}
                      </Button>
                    )}
                  </Flex>

                  {/* Close Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    minW="32px"
                    w="32px"
                    h="32px"
                    p="0"
                    borderRadius="8px"
                    onClick={() => handleDismiss(notification.id)}
                    _hover={{
                      bg: hoverBgGray,
                    }}
                  >
                    <Icon as={MdClose} w="18px" h="18px" />
                  </Button>
                </HStack>
              </Box>
            </ScaleFade>
          );
        })}
      </VStack>
    </Box>
  );
};

export default NotificationStack;
